import { useState } from 'react';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { isSsr } from '@99designs/browser';
import { CurrencyCode } from '@99designs/i18n';
import { products as productsConfig } from '../Product';
import { getRecommendedProducts } from './getRecommendedProducts';
import type { Product } from '../Product';
import type { WebOnboarderProductsQuery } from '../graphql/Recommendation.generated';

export function populateProducts(data: WebOnboarderProductsQuery) {
    return productsConfig().map((product) => {
        const pkg = data.products.find(({ key }) => key.includes(product.createKey));

        if (!pkg) return product;

        const productCopy = { ...product };

        const { title, description } = pkg;
        productCopy.name = title;
        productCopy.description = description;

        switch (pkg.__typename) {
            case 'ProductFixedPriceProject': {
                const { details, additionalDetails, pricing } = pkg;
                productCopy.features = details;
                productCopy.additionalDetails = additionalDetails;
                productCopy.pricing.fromPrice.amountInCents = pricing.amount.amountInCents;
                productCopy.pricing.fromPrice.currency = pricing.amount.currency as CurrencyCode;
                break;
            }
            case 'ProductContest': {
                const { contestDetails, pricing } = pkg;
                productCopy.features = contestDetails.map((detail) => {
                    return { title: detail };
                });
                productCopy.pricing.fromPrice.amountInCents = pricing.amount.amountInCents;
                productCopy.pricing.fromPrice.currency = pricing.amount.currency as CurrencyCode;

                break;
            }
            case 'ProductQuotedProject': {
                const { quotedProjectDetails, priceRange } = pkg;
                productCopy.features = quotedProjectDetails.map((detail) => {
                    return { title: detail };
                });

                if (priceRange.fromPrice) {
                    productCopy.pricing.fromPrice = {
                        amountInCents: priceRange.fromPrice,
                        currency: CurrencyCode.USD,
                    };
                }

                break;
            }
        }

        return productCopy;
    });
}

export function usePackageConfig() {
    const [productsLoading, setProductsLoading] = useState<boolean>(false);
    const [products, setProducts] = useState<Product[]>([]);
    const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;

    const keys = productsConfig().map((product) => product.createKey);

    const fetchRecommendedProducts = async (names: string[]) => {
        setProductsLoading(true);

        const data = await getRecommendedProducts(client, names);

        setProducts(populateProducts(data));
        setProductsLoading(false);
    };

    if (!isSsr() && !productsLoading && products.length === 0) {
        fetchRecommendedProducts(keys);
    }

    return products;
}
